import { uniq } from 'lodash';
import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import { selectableQuantityUnits } from '@shared/models/unit.model';
import { SupportedConversionUnits } from '@shared/helpers/conversion-factors.helpers';

/** Result factors that carry a user-selectable display unit. */
export type CostEmissionFactor = Extract<
  SupportedConversionUnits,
  'co2e' | 'sek'
>;

const factorUnits = [...selectableQuantityUnits, 'total', 'GFA'] as const;
export type FactorUnit = (typeof factorUnits)[number];

const factorUnitsSet: Set<string> = new Set(
  factorUnits,
) satisfies Set<FactorUnit>;

export const isFactorUnit = (unit: unknown): unit is FactorUnit =>
  factorUnitsSet.has(unit as FactorUnit);

/**
 * Per-factor priority list of preferred display units. The first entry that is
 * selectable in the current context wins, so prepending a unit makes it active.
 */
export type FactorUnitRecord = Record<CostEmissionFactor, FactorUnit[]>;

export const defaultFactorUnits: FactorUnitRecord = {
  co2e: ['GFA', 'total'],
  sek: ['GFA', 'total'],
};

const {
  useStore: usePreferredFactorUnits,
  set: setPreferredFactorUnits,
  get: getPreferredFactorUnits,
} = createLocalStorageStore<FactorUnitRecord>(
  'preferred_product_element_emission_units',
  defaultFactorUnits,
);

export {
  usePreferredFactorUnits,
  setPreferredFactorUnits,
  getPreferredFactorUnits,
};

/** High-level result display units offered in the scope selector. */
export const RESULT_DISPLAY_UNITS = ['total', 'GFA'] as const;
export type ResultDisplayUnit = (typeof RESULT_DISPLAY_UNITS)[number];

/**
 * The active high-level result display unit (total vs per m² GFA), derived from the
 * CO₂e factor's preferred unit so it stays in sync with {@link usePreferredFactorUnits}.
 */
export const useResultDisplayUnit = (): ResultDisplayUnit => {
  const preferred = usePreferredFactorUnits();
  const co2eUnit = preferred?.co2e?.[0] ?? defaultFactorUnits.co2e[0];
  return co2eUnit === 'GFA' ? 'GFA' : 'total';
};

/** Set the high-level result display unit for both CO₂e and cost factors at once. */
export const setResultDisplayUnit = (unit: ResultDisplayUnit): void => {
  const current = getPreferredFactorUnits() ?? defaultFactorUnits;
  setPreferredFactorUnits({
    co2e: uniq([unit, ...(current.co2e ?? defaultFactorUnits.co2e)]),
    sek: uniq([unit, ...(current.sek ?? defaultFactorUnits.sek)]),
  });
};
